import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Trans } from '@lingui/react/macro';
import AddIcon from '@mui/icons-material/Add';
import Stack from '@mui/material/Stack';
import { dispenserQueries } from '@/features/dispensers/queries.ts';
import { PageHeader } from '@/common/components/page-header/PageHeader.tsx';
import { DispenserList } from '@/features/dispensers/components/dispenser-list/DispenserList.tsx';
import {
  GridState,
  gridStateSearchSchema,
  gridStateToSieveParams,
  sieveParamsToGridState,
} from '@/common/api/sieveAdapter.ts';
import { DISPENSER_GRID_CONFIG } from '@/features/dispensers/components/dispenser-list/config.tsx';
import { RouterButtonLink } from '@/common/components/router-links/RouterButtonLink.tsx';
import { RouterFabLink } from '@/common/components/router-links/RouterFabLink.tsx';

export const Route = createFileRoute('/dispensers/')({
  component: Index,
  validateSearch: gridStateSearchSchema,
  loaderDeps: ({ search }) => sieveParamsToGridState(search, DISPENSER_GRID_CONFIG),
  loader: async ({ context, deps }) => {
    void context.queryClient.prefetchQuery(dispenserQueries.list(deps));
  },
});

function Index() {
  const navigate = useNavigate();
  const gridState = Route.useLoaderDeps();

  const handleGridStateChange = (params: GridState) => {
    void navigate({
      from: Route.fullPath,
      to: '.',
      search: gridStateToSieveParams(params),
    });
  };

  return (
    <Stack gap={4}>
      <PageHeader
        title={<Trans>Dispensers</Trans>}
        subtitle={<Trans>Manage and monitor your paint dispensing machines</Trans>}
        actions={
          <>
            <RouterButtonLink
              variant="contained"
              sx={{ display: { xs: 'none', sm: 'flex' } }}
              startIcon={<AddIcon />}
              to="/dispensers/new"
            >
              <Trans>Create</Trans>
            </RouterButtonLink>
            <RouterFabLink
              sx={{
                display: { xs: 'flex', sm: 'none' },
                position: 'fixed',
                bottom: 24,
                right: 16,
              }}
              color="primary"
              to="/dispensers/new"
            >
              <AddIcon />
            </RouterFabLink>
          </>
        }
      />
      <Stack sx={{ minHeight: 400 }}>
        <DispenserList gridState={gridState} onGridStateChange={handleGridStateChange} />
      </Stack>
    </Stack>
  );
}
